import type {HeaderData, HeaderNavItem} from '@commercial-website/data-layer'
import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import {topNavigationSignupActioned, TopNavigationSignupActionedAction} from '@/analytics/segment'
import {LocalizedLink, TrackedLocalizedLink} from '@/components/localized-link'
import type {Props as SignupProps} from '@/components/signup-modal'
import type {HeaderQuery} from '@/graphql-types'
import useHasMounted from '@/hooks/use-has-mounted'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useLocale} from '@/providers/locale'
import {usePageInfo} from '@/providers/page-info'
import {useSignupModal} from '@/providers/signup-modal'

import {HeaderV2} from './header-v2'

const AVAILABILITY_TEST_ID = 'availability-test'

export interface Props {
    hideHeader?: boolean
    logoOnly?: boolean
    ctaDestination?: 'signupform' | 'demopage' | 'partnersform' | 'accountingpartners'
    exploreProductDestination?: 'explorepartnerportal' | 'exploreproduct'
    genericBookADemo?: boolean
}

const TRACKED_ELEMENT_LOCATION = 'navbar header'

type CtaButtonParams = {
    ctaDestination: Props['ctaDestination']
    ctaButtonHref: string | undefined
    text: string
    location: {pathname: string} | null | undefined
    onSignupCtaClick: (signupProps?: SignupProps) => void
    className?: string
    disabled?: boolean
}

// The header (rebrand `ButtonLink`, legacy `NavigationMenu.Link`) already supplies the full CTA
// styling via `className`. Wrapping this in a Button component layers a second set of variant
// classes on top, which twMerge cannot strip where the two sets don't overlap (e.g. the legacy
// `hover:bg-*` survives because the rebrand pink variant has no hover background).
const buildCtaButton = ({
    ctaDestination,
    ctaButtonHref,
    text,
    location,
    onSignupCtaClick,
    disabled,
    className,
}: CtaButtonParams): React.ReactElement => {
    // Data-driven and shared with the Astro HeaderIsland: an explicit
    // `ctaButtonHref` navigates straight there; a blank href falls through to the
    // page-level `ctaDestination` behaviour (signup modal by default). Today only
    // en-us carries a href (`/us/getting-started`), so this preserves the previous
    // hardcoded en-us direct-nav while letting any locale opt into direct nav from
    // Sanity. `rawPath` because the seeded href is already locale-prefixed — the
    // consumer must not re-localize it.
    if (ctaButtonHref) {
        return (
            <TrackedLocalizedLink
                className={className}
                to={ctaButtonHref}
                rawPath
                trackingData={{
                    linkText: text,
                    linkType: 'cta',
                    elementLocation: TRACKED_ELEMENT_LOCATION,
                }}
                state={{prevPath: location?.pathname}}
            >
                {text}
            </TrackedLocalizedLink>
        )
    }

    switch (ctaDestination) {
        case 'signupform': {
            return (
                <button
                    type="button"
                    className={className}
                    onClick={() => onSignupCtaClick()}
                    aria-disabled={disabled}
                >
                    {text}
                </button>
            )
        }

        case 'accountingpartners': {
            return (
                <button
                    type="button"
                    className={className}
                    onClick={() =>
                        onSignupCtaClick({
                            signupProps: {
                                form: 'Partner',
                                isPartner: true,
                            },
                        })
                    }
                    aria-disabled={disabled}
                >
                    {text}
                </button>
            )
        }

        case 'demopage': {
            return (
                <TrackedLocalizedLink
                    className={className}
                    to="/demo"
                    trackingData={{
                        linkText: text,
                        linkType: 'cta',
                        elementLocation: TRACKED_ELEMENT_LOCATION,
                    }}
                >
                    {text}
                </TrackedLocalizedLink>
            )
        }

        case 'partnersform': {
            return (
                <LocalizedLink
                    className={className}
                    to="/bigform-partner"
                    onClick={() =>
                        topNavigationSignupActioned({
                            action: TopNavigationSignupActionedAction.Partners,
                        })
                    }
                >
                    {text}
                </LocalizedLink>
            )
        }

        default: {
            throw new Error('Unrecognized destination')
        }
    }
}

const HIDE_ON_VALUES: ReadonlyArray<HeaderNavItem['hideOn']> = [
    'never',
    'mobile',
    'tablet',
    'laptop',
    'all',
]

// Gatsby's GraphQL widens the pinned `HeaderData` node fields (e.g. `target`, `hideOn`) back to
// plain strings, so re-narrow them to the frozen `HeaderData` union types before handing the
// content to `HeaderV2` (which already consumes normalised `HeaderData`).
const toHeaderData = (node: HeaderQuery['allHeaderData']['nodes'][number]): HeaderData => ({
    logInText: node.logInText,
    ctaButtonText: node.ctaButtonText,
    ctaButtonHref: node.ctaButtonHref || undefined,
    secondaryCtaText: node.secondaryCtaText || undefined,
    secondaryCtaHref: node.secondaryCtaHref || undefined,
    items: node.items.map((item) => ({
        label: item.label,
        href: item.href || undefined,
        target: item.target === '_blank' ? '_blank' : undefined,
        hideOn: HIDE_ON_VALUES.includes(item.hideOn as HeaderNavItem['hideOn'])
            ? (item.hideOn as HeaderNavItem['hideOn'])
            : 'never',
        groups: (item.groups ?? []).map((group) => ({
            label: group.label,
            links: group.links.map((link) => ({
                text: link.text,
                subText: link.subText || undefined,
                href: link.href,
                target: link.target === '_blank' ? '_blank' : undefined,
            })),
        })),
    })),
})

const HeaderContainer = ({hideHeader, ...props}: Props) => {
    const data = useStaticQuery<HeaderQuery>(query)
    const [node] = useLocalizedContent(data.allHeaderData.nodes)

    const hasMounted = useHasMounted()
    const currentLocale = useLocale()
    const {location} = usePageInfo()
    const {renderSignupModal} = useSignupModal()

    if (!node) {
        throw new Error(`Missing header content for locale ${currentLocale.code}`)
    }

    const headerV2Content = toHeaderData(node)
    const ctaButtonText = headerV2Content.ctaButtonText

    const onSignupCtaClick = (signupProps?: SignupProps) => {
        renderSignupModal(signupProps)
        topNavigationSignupActioned({action: TopNavigationSignupActionedAction.Started})
    }

    if (hideHeader) {
        return null
    }

    return (
        <HeaderV2
            content={headerV2Content}
            ctaButton={({className}) =>
                buildCtaButton({
                    ctaDestination: props.ctaDestination ?? 'signupform',
                    ctaButtonHref: headerV2Content.ctaButtonHref,
                    text: ctaButtonText,
                    location,
                    onSignupCtaClick,
                    className,
                    disabled: !hasMounted,
                })
            }
            logoOnly={props.logoOnly}
            testId={AVAILABILITY_TEST_ID}
        />
    )
}

const query = graphql`
    query Header {
        allHeaderData {
            nodes {
                lang
                logInText
                ctaButtonText
                ctaButtonHref
                secondaryCtaText
                secondaryCtaHref
                items {
                    label
                    href
                    target
                    hideOn
                    groups {
                        label
                        links {
                            text
                            subText
                            href
                            target
                        }
                    }
                }
            }
        }
    }
`

export default HeaderContainer
